import { labelBody, labelFooterRow, labelHeader } from '@/lib/labelLayout'
import { equipmentScanCode } from '@/lib/equipmentScanCode'

/**
 * The asset tag that goes on a machine.
 *
 * How it looks lives in `labelLayout.ts`, shared with the inventory labels, so a
 * technician reads all three the same way round.
 */

/**
 * The tag's encoding lives in `equipmentScanCode.ts` and is re-exported here so
 * this module stays the one place you look for "the equipment label". It was
 * moved because the mobile scan flow must resolve a scanned tag, and everything
 * else in this file reaches `bwip-js` through `labelLayout` — a symbol
 * generator the phone never calls but would have to download.
 */
export { equipmentScanCode, equipmentIdFromScan } from '@/lib/equipmentScanCode'

export interface EquipmentLabelInput {
  /** Immutable `Equipment.id`. What the symbol encodes. */
  id: string
  /** `Equipment.label` — the "Equipment ID" a person uses, e.g. `APH-A4`. */
  label: string
  typeName?: string | null
  serial?: string | null
  /** Where it is ("NYP › Building 1 › 2nd Floor › Room 5"): the mapped place, else the old free text. */
  place?: string | null
  status?: string | null
  /** What the tag says the thing is: "Equipment" (the default) or "Room" (z8nrz7ewt3). */
  role?: string
}

/** An equipment asset tag: which machine this is, and the symbol that proves it. */
export function equipmentLabelHtml(input: EquipmentLabelInput): string {
  const code = equipmentScanCode(input.id)
  const where = input.place ?? ''

  // Serial rides in the header: it identifies the physical box for maintenance
  // and warranty, but it is not what anyone reaches for to find the machine.
  const meta = input.serial ? `SN ${input.serial}` : ''
  const sub = [input.typeName, input.status === 'OUT_OF_SERVICE' ? 'Out of service' : null]
    .filter(Boolean)
    .join(' · ')

  return (
    `<div class="lbl">` +
    labelHeader(input.role ?? 'Equipment', meta) +
    labelBody(code, input.label, sub) +
    labelFooterRow('Location', where || 'Not sited', 'path') +
    `</div>`
  )
}
